import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import type { UserStateEntry, UserStateMap, UserStateItemKey } from "../../../types/user-state";

export interface UserStateSliceState {
  entries: UserStateMap;
  hydratedFromSSR: boolean;
}

const initialState: UserStateSliceState = {
  entries: {},
  hydratedFromSSR: false,
};

const userStateSlice = createSlice({
  name: "userState",
  initialState,
  reducers: {
    hydrateFromSSR(state, action: PayloadAction<UserStateMap>) {
      state.entries = { ...action.payload };
      state.hydratedFromSSR = true;
    },
    mergeEntries(state, action: PayloadAction<UserStateMap>) {
      state.entries = {
        ...state.entries,
        ...action.payload,
      };
    },
    setEntry(
      state,
      action: PayloadAction<{ itemKey: UserStateItemKey; entry: UserStateEntry }>
    ) {
      state.entries[action.payload.itemKey] = action.payload.entry;
    },
    replaceAll(state, action: PayloadAction<UserStateMap>) {
      state.entries = { ...action.payload };
    },
    removeEntry(state, action: PayloadAction<UserStateItemKey>) {
      delete state.entries[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      const incoming = action.payload?.userState as UserStateSliceState | undefined;
      if (!incoming?.entries) {
        return;
      }
      state.entries = {
        ...incoming.entries,
        ...state.entries,
      };
    });
  },
});

export const { hydrateFromSSR, mergeEntries, setEntry, replaceAll, removeEntry } = userStateSlice.actions;
export const userStateReducer = userStateSlice.reducer;

export const userStateSelectors = {
  selectAll: (state: UserStateSliceState["entries"]) => state,
  selectByKey: (state: UserStateSliceState["entries"], itemKey: UserStateItemKey) =>
    state[itemKey],
};

